import { StructureDto } from '~/backend/connector-api/connector-schemas';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DoseTargetData } from './doseTargetsTransforms';

const PTV_REGEX_PATTERN = /^PTV_(\d+)$/;

const excludedTypes: RoiInterpretedType[] = [
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.BOLUS,
    RoiInterpretedType.FIXATION,
    RoiInterpretedType.SUPPORT,
    RoiInterpretedType.AVOIDANCE,
];

const targetOrder: RoiInterpretedType[] = [
    RoiInterpretedType.PTV,
    RoiInterpretedType.CTV,
    RoiInterpretedType.GTV,
];

/**
 * Maps a session structure 'type' string to the domain
 * RoiInterpretedType enum. Falls back to UNKNOWN for
 * unrecognized types.
 */
function mapSessionTypeToRoiInterpretedType(type: string): RoiInterpretedType {
    const upperType = type.toUpperCase();
    const matchedValue = Object.values(RoiInterpretedType).find(
        (enumValue) => enumValue === upperType
    );
    return matchedValue ?? RoiInterpretedType.UNKNOWN;
}

function parseDoseFromName(name: string): number {
    const match = name.match(PTV_REGEX_PATTERN);
    if (!match) {
        return 0;
    }
    const doseValue = parseFloat(match[1]);
    return doseValue >= 100 ? doseValue / 100 : doseValue;
}

/**
 * Transforms session structure DTOs into DoseTargetData[],
 * applying the same filtering, mapping, and sorting logic
 * as transformStructuresToDoseTargets but operating on the
 * session payload shape instead of RoiReadModel[].
 *
 * Used as a fallback to show structure data in the
 * DoseStructuresTable before DICOM files finish loading.
 */
export default function transformSessionStructuresToDoseTargets(
    structures: ReadonlyArray<StructureDto>
): DoseTargetData[] {
    return structures
        .filter((structure) => {
            const interpretedType = mapSessionTypeToRoiInterpretedType(
                structure.type
            );
            return (
                !structure.is_empty && !excludedTypes.includes(interpretedType)
            );
        })
        .map((structure) => ({
            name: structure.name,
            isTarget: PTV_REGEX_PATTERN.test(structure.name),
            doseQuantity: parseDoseFromName(structure.name),
            interpretedType: mapSessionTypeToRoiInterpretedType(structure.type),
        }))
        .sort((a, b) => {
            const aPriority = targetOrder.indexOf(a.interpretedType);
            const bPriority = targetOrder.indexOf(b.interpretedType);
            if (aPriority !== -1 && bPriority !== -1) {
                return a.name.localeCompare(b.name);
            } else if (aPriority !== -1) {
                return -1;
            } else if (bPriority !== -1) {
                return 1;
            } else {
                return a.name.localeCompare(b.name);
            }
        });
}
